import { Controller } from "@hotwired/stimulus"
import { loadMapLibre, loadBaseStyle, createPin } from "../lib/maplibre"
import { whenIdle } from "../lib/idle"

export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    title: String,
    color: String,
    icon: String
  }

  connect() {
    // The map sits below the event's details: only fetch MapLibre and tiles when it's about to scroll
    // into view, and never before the page itself has finished loading
    if (!("IntersectionObserver" in window)) return whenIdle(() => this.element.isConnected && this.load())

    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect()
        whenIdle(() => this.element.isConnected && this.load())
      }
    }, { rootMargin: "300px 0px" })
    this.observer.observe(this.element)
  }

  async load() {
    const generation = this.generation = (this.generation || 0) + 1

    try {
      const [maplibregl, style] = await Promise.all([loadMapLibre(), loadBaseStyle()])
      if (generation !== this.generation) return

      this.initializeMap(maplibregl, style)
    } catch (error) {
      // Without the map (offline, no WebGL) the address below still says where it is, so drop the empty frame
      console.warn("Map unavailable:", error)
      this.element.hidden = true
    }
  }

  initializeMap(maplibregl, style) {
    const center = [this.lngValue, this.latValue]

    // Turbo's cached page snapshots can include the previous map
    this.element.replaceChildren()
    this.map = new maplibregl.Map({
      container: this.element,
      style,
      center,
      zoom: 15,
      attributionControl: { compact: true },
      // One finger scrolls the page on phones; two fingers (or ctrl + scroll) move the map
      cooperativeGestures: true,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false
    })
    this.map.touchZoomRotate.disableRotation()
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")

    const pin = createPin({ color: this.colorValue, icon: this.iconValue, label: this.titleValue, interactive: false })
    new maplibregl.Marker({ element: pin, anchor: "bottom" })
      .setLngLat(center)
      .addTo(this.map)
  }

  disconnect() {
    this.observer?.disconnect()
    this.generation = (this.generation || 0) + 1
    this.map?.remove()
    this.map = null
  }
}
